/**
 * Renders the "Similar Homes Available" section shown under a Sold Home Card (NHS20-8827) —
 * styled per the Figma "Sold Card / Master" component, including desktop's collapsible behavior
 * (mobile shares the same collapse/expand markup — see plugins/listView.plugin.js's
 * setupSimilarHomesToggleListener, which handles the click for both). Which card starts expanded
 * is decided by the caller (services/listViewContentService.js), not here — this only renders
 * whatever `expanded` it's given.
 *
 * @module nativeInteractiveSiteMap/components/similarHomesSection.dom
 */

import { chevronDownIconSvg } from 'constants/svg-assets';
import LotStatus from '../enums/lotStatus';

/**
 * @param {object} candidate - redux/selectors.js#selectSimilarHomesCandidates entry.
 * @returns {string} 'Floor plan', 'Model home', or 'Quick move-in' (NHS20-8827 QA: sentence case,
 *   matching the Figma "Sold Card / Master" spec — not the Title Case used by the badge on other
 *   card types elsewhere in the app).
 */
function getCandidateTypeLabel(candidate) {
  if (candidate.isFloorPlan) {
    return 'Floor plan';
  }
  return candidate.lotStatus === LotStatus.Model.toString() ? 'Model home' : 'Quick move-in';
}

/**
 * Modifier for the shared .nhs-c-siteplan-card__badge component (already used for this same
 * purpose by components/quickMoveInCards.dom.js and floorPlanCards.dom.js) — kept as its own
 * branch on candidate shape rather than derived from getCandidateTypeLabel's string, so a future
 * label wording change can't silently break the badge color.
 *
 * @param {object} candidate - redux/selectors.js#selectSimilarHomesCandidates entry.
 * @returns {string} 'floorplan', 'model', or 'quick-move'.
 */
function getCandidateBadgeModifier(candidate) {
  if (candidate.isFloorPlan) {
    return 'floorplan';
  }
  return candidate.lotStatus === LotStatus.Model.toString() ? 'model' : 'quick-move';
}

/**
 * @param {object} candidate - redux/selectors.js#selectSimilarHomesCandidates entry.
 * @returns {string} HTML string for one card.
 */
function renderSimilarHomeCard(candidate) {
  const typeLabel = getCandidateTypeLabel(candidate);

  // Same identity-attribute convention as components/quickMoveInCards.dom.js and
  // floorPlanCards.dom.js — picked up by the existing card-click delegation in
  // plugins/floorPlanDetail.plugin.js / housingUnitDetail.plugin.js (NHS20-8827: clicking a
  // similar home opens its detail page), and incidentally by handlers/cardHoverInteractionHandlers.js
  // for the map lot-highlight-on-hover behavior every other identity-attributed card already gets.
  const identityAttr = candidate.isFloorPlan
    ? `data-plan-id="${candidate.planId}"`
    : `data-housing-unit-id="${candidate.specificationId}"`;

  const lotLabelHtml = candidate.lotDisplayLabel
    ? `<span class="nhs-c-siteplan-card__badge" data-qa="native_ism_similar_home_lot_label">Lot ${candidate.lotDisplayLabel}</span>`
    : '';

  const specs = [
    candidate.bedrooms != null ? `${candidate.bedrooms} Bd` : null,
    candidate.formattedBathrooms != null ? `${candidate.formattedBathrooms} Ba` : null,
    candidate.garage != null ? `${Math.round(candidate.garage)} Gr` : null,
    candidate.formattedSquareFeet != null ? `${candidate.formattedSquareFeet} SqFt` : null,
  ].filter(Boolean);

  const specsHtml = specs
    .map((spec) => `<span class="nhs-c-native-interactive-site-map__spec nhs-mq-native-interactive-site-map__spec-medium" data-qa="native_ism_similar_home_spec">${spec}</span>`)
    .join('');

  return `
    <article class="nhs-c-native-interactive-site-map__similar-home-card" ${identityAttr} data-similar-home-card data-qa="native_ism_similar_home_card">
      <div class="nhs-c-native-interactive-site-map__similar-home-badges">
        <span class="nhs-c-siteplan-card__badge nhs-c-siteplan-card__badge--${getCandidateBadgeModifier(candidate)}" data-qa="native_ism_similar_home_type_label">${typeLabel}</span>
        ${lotLabelHtml}
      </div>
      <p class="nhs-c-native-interactive-site-map__similar-home-name nhs-mq-native-interactive-site-map__similar-home-name-medium" data-qa="native_ism_similar_home_name">${candidate.name ?? ''}</p>
      <p class="nhs-c-native-interactive-site-map__similar-home-type nhs-mq-native-interactive-site-map__similar-home-type-medium" data-qa="native_ism_similar_home_type">${typeLabel}</p>
      ${candidate.formattedPrice != null ? `<p class="nhs-c-native-interactive-site-map__similar-home-price nhs-mq-native-interactive-site-map__similar-home-price-medium" data-qa="native_ism_similar_home_price">${candidate.formattedPrice}</p>` : ''}
      ${specsHtml ? `<div class="nhs-c-native-interactive-site-map__spec-list" data-qa="native_ism_similar_home_specs">${specsHtml}</div>` : ''}
    </article>
  `;
}

/**
 * @param {object[]} similarHomes - Up to 2 entries from services/similarHomesService.js.
 * @param {object} [options]
 * @param {boolean} [options.expanded] - Whether the list starts expanded (NHS20-8827/desktop's
 *   collapsible behavior: only the first sold-card-group on screen starts expanded — decided by
 *   services/listViewContentService.js, not here).
 * @returns {string} HTML string, or '' when there are no matches (NHS20-8827: "If no similar
 *   homes exist, do not display the section").
 */
export default function renderSimilarHomesSection(similarHomes = [], { expanded = false } = {}) {
  if (similarHomes.length === 0) {
    return '';
  }

  const expandedClass = expanded ? ' nhs-c-native-interactive-site-map__similar-homes-section--expanded' : '';

  return `
    <section class="nhs-c-native-interactive-site-map__similar-homes-section${expandedClass}" data-similar-homes-section data-qa="native_ism_similar_homes_section">
      <div class="nhs-c-native-interactive-site-map__sold-card-divider"></div>
      <button
        type="button"
        class="nhs-c-native-interactive-site-map__similar-homes-toggle"
        data-similar-homes-toggle
        aria-expanded="${expanded}"
        data-qa="native_ism_similar_homes_toggle"
      >
        <span class="nhs-c-native-interactive-site-map__similar-homes-label nhs-mq-native-interactive-site-map__similar-homes-label-medium">Similar homes available</span>
        <span class="nhs-c-native-interactive-site-map__similar-homes-chevron" aria-hidden="true">${chevronDownIconSvg}</span>
      </button>
      <div class="nhs-c-native-interactive-site-map__similar-homes-list" data-similar-homes-list>
        ${similarHomes.map(renderSimilarHomeCard).join('')}
      </div>
    </section>
  `;
}
